import axios from 'axios';
import aiDevelopmentSnapshot from '../data/aiDevelopment';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  if (process.env.NODE_ENV === 'production') {
    return '';
  }
  const { protocol, hostname } = window.location;
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    return `${protocol}//${hostname}:8000`;
  }
  return 'http://localhost:8000';
};

// User management service URL (auth & admin endpoints)
const getUserMgmtBaseUrl = () => {
  if (process.env.REACT_APP_USER_MGMT_URL) {
    return process.env.REACT_APP_USER_MGMT_URL;
  }
  if (process.env.NODE_ENV === 'production') {
    // Production: use /user-mgmt prefix, Nginx will proxy to user-management service
    return '/user-mgmt';
  }
  const { protocol, hostname } = window.location;
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    return `${protocol}//${hostname}:8001`;
  }
  return 'http://localhost:8001';
};

const API_BASE_URL = getApiBaseUrl();
const USER_MGMT_BASE_URL = getUserMgmtBaseUrl();
const LS_TOKEN_KEY = 'auth_token';

// Module-level cache (persists across component unmounts / route navigations)
const _cache = new Map();

const CACHE_TTL = {
  fast:   2 * 60_000,   // 2 min — market quotes, charts
  medium: 5 * 60_000,   // 5 min — page data, feeds, events
  slow:  15 * 60_000,   // 15 min — ARK, investors, summaries
};

class ApiService {
  constructor() {
    const apiKey = process.env.REACT_APP_API_KEY || '';
    const headers = { 'Content-Type': 'application/json' };
    if (apiKey) headers['X-API-Key'] = apiKey;

    // Main API client (AIFlow backend)
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers,
    });

    // User management client (user-management service)
    this.userMgmtClient = axios.create({
      baseURL: USER_MGMT_BASE_URL,
      timeout: 30000,
      headers: { 'Content-Type': 'application/json' },
    });

    // Setup interceptors for both clients
    [this.client, this.userMgmtClient].forEach((client) => {
      // Request interceptor: add auth token
      client.interceptors.request.use(
        (config) => {
          const token = localStorage.getItem(LS_TOKEN_KEY);
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          return config;
        },
        (error) => Promise.reject(error)
      );

      // Response interceptor: unwrap data and handle 401
      client.interceptors.response.use(
        (response) => response.data,
        (error) => {
          console.error('API Error:', error);
          if (error.response?.status === 401) {
            // Clear auth on unauthorized
            localStorage.removeItem(LS_TOKEN_KEY);
            localStorage.removeItem('auth_user');
            // Optionally redirect to login
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
          return Promise.reject(error);
        }
      );
    });
  }

  // --- Cache helpers ---

  async _cachedGet(cacheKey, url, params, ttl) {
    const entry = _cache.get(cacheKey);
    if (entry && Date.now() - entry.ts < ttl) {
      return entry.data;
    }
    const data = await this.client.get(url, { params });
    _cache.set(cacheKey, { data, ts: Date.now() });
    return data;
  }

  peekCache(key) {
    const entry = _cache.get(key);
    if (!entry) return null;
    return entry.data;
  }

  // Homepage
  async getHomepageData(marketSymbols) {
    const params = marketSymbols ? { market_symbols: marketSymbols } : {};
    return this._cachedGet('homepage', '/api/homepage/data', params, CACHE_TTL.medium);
  }

  // Market
  async getMarketSnapshot(symbols) {
    const params = symbols ? { symbols } : {};
    const key = `snapshot:${symbols || 'all'}`;
    return this._cachedGet(key, '/api/market/snapshot', params, CACHE_TTL.fast);
  }

  async getMarketChart(symbol, period = '1d') {
    const key = `chart:${symbol}:${period}`;
    return this._cachedGet(key, `/api/market/chart/${symbol}`, { period }, CACHE_TTL.fast);
  }

  async getUpcomingEvents(days = 14) {
    const key = `events:${days}`;
    return this._cachedGet(key, '/api/market/events', { days }, CACHE_TTL.medium);
  }

  // Feed
  async getFeeds(params = {}) {
    // Only cache first page (offset=0)
    if (!params.offset || params.offset === 0) {
      const key = `feeds:${params.source || 'All'}`;
      return this._cachedGet(key, '/api/v1/feeds', params, CACHE_TTL.medium);
    }
    return this.client.get('/api/v1/feeds', { params });
  }

  async searchFeeds(query, limit = 20) {
    return this.client.post('/api/vector-search/feeds', { query, limit });
  }

  async getFeedById(feedId) {
    return this.client.get(`/api/v1/feeds/${feedId}`);
  }

  async translateFeed(feedId) {
    return this.client.post(`/api/v1/feeds/${feedId}/translate`);
  }

  // ARK
  async getArkTrades(limit = 10) {
    return this.client.get('/api/ark/trades', { params: { limit } });
  }

  async getArkSummary() {
    return this.client.get('/api/ark/summary');
  }

  // Investors
  async getInvestorsList() {
    return this._cachedGet('investors-list', '/api/investors/list', {}, CACHE_TTL.slow);
  }

  async getInvestorHoldings(key, limit = 10) {
    return this.client.get(`/api/investors/${key}/holdings`, { params: { limit } });
  }

  // Companies
  async getPopularCompanies() {
    return this.client.get('/api/v1/companies/popular');
  }
  async getCompanyProfile(symbol) {
    return this.client.get(`/api/v1/companies/${symbol}`);
  }
  async getCompanyNews(symbol, limit = 10) {
    return this.client.get(`/api/v1/companies/${symbol}/news`, { params: { limit } });
  }
  async getCompanyAnalysis(symbol, days = 7, language = 'en') {
    return this.client.get(`/api/v1/companies/${symbol}/analysis`, { params: { days, language } });
  }

  // ARK Holdings
  async getArkHoldings(fund = 'ARKK', limit = 15) {
    const key = `ark-holdings:${fund}`;
    return this._cachedGet(key, `/api/ark/holdings/${fund}`, { limit }, CACHE_TTL.slow);
  }

  async getArkChanges(fund, days = 7) {
    const params = { days };
    if (fund) params.fund = fund;
    const key = `ark-changes:${fund || 'all'}:${days}`;
    return this._cachedGet(key, '/api/ark/changes', params, CACHE_TTL.slow);
  }

  async getInvestorInfo(key) {
    return this.client.get(`/api/investors/${key}/info`);
  }

  // HackerNews
  async getHackerNewsTop(limit = 30) {
    return this._cachedGet('hackernews', '/api/hackernews/top', { limit }, CACHE_TTL.slow);
  }

  // AI Events
  async getAIEvents(limit = 30) {
    const key = `ai-events:${limit}`;
    return this._cachedGet(key, '/api/data-sources/ai-events', { limit }, CACHE_TTL.slow);
  }

  async getAIEventById(eventId) {
    return this.client.get(`/api/v1/ai-events/${eventId}`);
  }

  async translateAIEvent(eventId) {
    return this.client.post(`/api/v1/ai-events/${eventId}/translate`);
  }

  async getAIEventsSummary(days = 7) {
    const key = `ai-events-summary:${days}`;
    return this._cachedGet(key, '/api/data-sources/ai-events-summary', { days }, CACHE_TTL.slow);
  }

  async getAIFutureDashboard() {
    return aiDevelopmentSnapshot;
  }

  // 市场简报
  async getDailySummary(session = 'postmarket') {
    const key = `daily-summary:${session}`;
    return this._cachedGet(key, '/api/v1/daily-summary', { session }, CACHE_TTL.slow);
  }

  // 科技新闻汇总
  async getTechNewsSummary(days = 7) {
    const key = `tech-news-summary:${days}`;
    return this._cachedGet(key, '/api/data-sources/tech-news-summary', { days }, CACHE_TTL.slow);
  }

  // Deep Dive Articles
  async getDeepDiveArticles(params = {}) {
    return this.client.get('/api/deep-dives', { params });
  }

  async getArticleById(articleId) {
    return this.client.get(`/api/deep-dives/${articleId}`);
  }

  // Authentication (via user-management service)
  async register(email, password) {
    return this.userMgmtClient.post('/api/auth/register', { email, password });
  }

  async login(email, password) {
    return this.userMgmtClient.post('/api/auth/login', { email, password });
  }

  async logout() {
    return this.userMgmtClient.post('/api/auth/logout');
  }

  async changePassword(oldPassword, newPassword) {
    return this.userMgmtClient.post('/api/auth/change-password', { old_password: oldPassword, new_password: newPassword });
  }

  async getCurrentUser() {
    return this.userMgmtClient.get('/api/auth/me');
  }

  // Admin - User Management (via user-management service)
  async getUsers() {
    return this.userMgmtClient.get('/api/admin/users');
  }

  async resetUserPassword(userId, newPassword) {
    return this.userMgmtClient.post(`/api/admin/users/${userId}/reset-password`, { new_password: newPassword });
  }

  async disableUser(userId) {
    return this.userMgmtClient.post(`/api/admin/users/${userId}/disable`);
  }

  async enableUser(userId) {
    return this.userMgmtClient.post(`/api/admin/users/${userId}/enable`);
  }

  // User preferences (via user-management service)
  async getUserPreferences() {
    return this.userMgmtClient.get('/api/user/preferences');
  }

  async saveUserPreferences(prefs) {
    return this.userMgmtClient.put('/api/user/preferences', prefs);
  }

}

const apiService = new ApiService();
export default apiService;
